import { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

// Admin staff share a single central login, so the creator of a package is the
// name they pick on Add Package rather than the signed-in uid. The list of
// names lives in one Firestore document so it can be edited from the console
// without a redeploy:
//
//   config/staff  ->  { names: ["Jane Doe", "John Roe", ...] }
//
// These strings are what gets written to package.createdBy and what the
// "Package Creation by Staff Member" report groups on, so renaming an entry
// orphans that person's existing packages - prefer adding over editing.
export const STAFF_CONFIG_COLLECTION = 'config';
export const STAFF_CONFIG_DOC = 'staff';
export const STAFF_CONFIG_PATH = `${STAFF_CONFIG_COLLECTION}/${STAFF_CONFIG_DOC}`;

// Read the staff names, ignoring blanks/duplicates and sorting alphabetically
// so the dropdown order does not depend on how the array was typed in.
export const fetchStaffNames = async () => {
  const snapshot = await getDoc(doc(db, STAFF_CONFIG_COLLECTION, STAFF_CONFIG_DOC));
  const names = snapshot.exists() ? snapshot.data().names : null;
  if (!Array.isArray(names)) return [];

  const cleaned = names
    .filter(name => typeof name === 'string' && name.trim())
    .map(name => name.trim());

  return [...new Set(cleaned)].sort((a, b) => a.localeCompare(b));
};

export const useStaffNames = () => {
  const [staffNames, setStaffNames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const loadStaffNames = async () => {
      try {
        const names = await fetchStaffNames();
        if (!cancelled) setStaffNames(names);
      } catch (err) {
        console.error('Error fetching staff names:', err);
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadStaffNames();
    return () => { cancelled = true; };
  }, []);

  return { staffNames, loading, error };
};

export default useStaffNames;
